import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { QuotaRepository } from '@asm/features/sync/repositories/quota.repository';
import { ProductRepository } from '@asm/features/sync/repositories/product.repository';
import { WarehouseRepository } from '@asm/features/sync/repositories/warehouse.repository';
import { QuotaItem, QuotaSearchResult } from '@asm/features/quick-order/models/quota.model';

@Injectable({ providedIn: 'root' })
export class DefaultLocalQuotaService {
  private readonly quotaRepo = inject(QuotaRepository);
  private readonly productRepo = inject(ProductRepository);
  private readonly warehouseRepo = inject(WarehouseRepository);

  /**
   * Retorna uma página de cotas do requester atual lidas do IndexedDB,
   * enriquecendo cada cota com nome do produto e depósito.
   *
   * @param page     Número da página (1-based)
   * @param pageSize Itens por página
   */
  async getQuotasByRequester(page: number, pageSize: number): Promise<QuotaSearchResult> {
    const [allQuotas, allProducts, allWarehouses] = await Promise.all([
      firstValueFrom(this.quotaRepo.getAll()),
      firstValueFrom(this.productRepo.getAll()),
      firstValueFrom(this.warehouseRepo.getAll()),
    ]);

    const productMap = new Map(allProducts.map(p => [p.code, p]));
    const warehouseMap = new Map(allWarehouses.map(w => [w.code, w]));

    const items: QuotaItem[] = allQuotas.map(quota => {
      const product = productMap.get(quota.productId);
      const warehouse = warehouseMap.get(quota.warehouseId ?? '');

      return {
        id: quota.id,
        desiredDate: new Date(quota.desiredDate),
        approvedDate: new Date(quota.desiredDate),
        requestedQty: quota.approvedQty ?? 0,
        approvedQty: quota.approvedQty,
        consumedQty: quota.consumedQty,
        status: quota.status,
        balance: quota.balance,
        b2bUnit: quota.b2bUnitId,
        product: (product?.sapName ?? product?.name ?? quota.productId) || '-',
        warehouse: (warehouse?.description ?? quota.warehouseId ?? '') || '-',
      };
    });

    const total = items.length;
    const totalPages = Math.ceil(total / pageSize) || 1;
    const safePage = Math.max(1, Math.min(page, totalPages));
    const start = (safePage - 1) * pageSize;
    const paginatedItems = items.slice(start, start + pageSize);

    return {
      items: paginatedItems,
      total,
      page: safePage,
      pageSize,
      totalPages,
    };
  }
}
